import { theme, formatMoney, subscribe, PUB_SUB_EVENTS, showToast } from './global.js';
import { getWishlist, toggleWishlist } from './wishlist-compare.js';
import { addToCart } from './cart.js';

// Wishlist page: the saved handles are turned into real product cards (same markup as everywhere else),
// with a total, sort, in-stock filter, add-all, share link and clear-all.
const grid = document.querySelector('[data-wishlist-grid]');
const toolbar = document.querySelector('[data-wishlist-toolbar]');
const emptyEl = document.querySelector('[data-wishlist-empty]');
const loadingEl = document.querySelector('[data-wishlist-loading]');
const countEl = document.querySelector('[data-wishlist-count-text]');
const totalEl = document.querySelector('[data-wishlist-total]');
const sortEl = document.querySelector('[data-wishlist-sort]');
const stockBtn = document.querySelector('[data-wishlist-instock]');

let items = []; // { handle, product, html, order }
let inStockOnly = false;

// A shared link (?items=a,b,c) shows that list when the visitor has none of their own.
function sharedHandles() {
  const raw = new URLSearchParams(window.location.search).get('items');
  return raw ? raw.split(',').map((h) => h.trim()).filter(Boolean).slice(0, 40) : [];
}

async function load(handle, order) {
  const [product, cardHtml] = await Promise.all([
    fetch(`/products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    fetch(`/products/${handle}?section_id=product-card-render`).then((r) => (r.ok ? r.text() : '')).catch(() => ''),
  ]);
  if (!product) return null;
  const card = new DOMParser().parseFromString(cardHtml, 'text/html').querySelector('.product-card');
  return card ? { handle, product, html: card.outerHTML, order } : null;
}

function visibleItems() {
  let list = items.filter((i) => !inStockOnly || i.product.available);
  const sort = sortEl?.value || 'recent';
  if (sort === 'price-asc') list = [...list].sort((a, b) => a.product.price - b.product.price);
  if (sort === 'price-desc') list = [...list].sort((a, b) => b.product.price - a.product.price);
  if (sort === 'title') list = [...list].sort((a, b) => a.product.title.localeCompare(b.product.title));
  if (sort === 'recent') list = [...list].sort((a, b) => b.order - a.order);
  return list;
}

function draw() {
  const list = visibleItems();
  grid.innerHTML = list
    .map((item, index) => `<div class="wishlist-item" data-wishlist-item data-handle="${item.handle}" style="--item-delay:${Math.min(index, 12) * 0.05}s">${item.html}</div>`)
    .join('');
  const total = items.reduce((sum, i) => sum + i.product.price, 0);
  if (countEl) countEl.textContent = items.length;
  if (totalEl) totalEl.textContent = formatMoney(total, theme.moneyFormat);
  grid.hidden = list.length === 0;
  document.dispatchEvent(new CustomEvent('wishlist:rendered'));
  // Re-apply the heart state on the freshly drawn cards.
  grid.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => btn.setAttribute('aria-pressed', 'true'));
}

function showEmpty(isEmpty) {
  emptyEl.hidden = !isEmpty;
  toolbar.hidden = isEmpty;
  if (isEmpty) grid.hidden = true;
}

async function init() {
  let handles = getWishlist();
  if (!handles.length) handles = sharedHandles();
  if (!handles.length) {
    loadingEl.hidden = true;
    showEmpty(true);
    return;
  }
  const loaded = (await Promise.all(handles.map((h, index) => load(h, index)))).filter(Boolean);
  loadingEl.hidden = true;
  items = loaded;
  if (!items.length) {
    showEmpty(true);
    return;
  }
  showEmpty(false);
  draw();
}

function removeHandle(handle) {
  const el = grid.querySelector(`[data-wishlist-item][data-handle="${handle}"]`);
  items = items.filter((i) => i.handle !== handle);
  const finish = () => {
    el?.remove();
    if (!items.length) showEmpty(true);
    else {
      if (countEl) countEl.textContent = items.length;
      if (totalEl) totalEl.textContent = formatMoney(items.reduce((s, i) => s + i.product.price, 0), theme.moneyFormat);
    }
  };
  if (el) {
    el.classList.add('is-removing');
    setTimeout(finish, 320);
  } else {
    finish();
  }
}

// A heart tapped on a card removes it from the page (the list itself is updated by wishlist-compare.js).
subscribe(PUB_SUB_EVENTS.wishlistUpdate, ({ list }) => {
  items.filter((i) => !list.includes(i.handle)).forEach((i) => removeHandle(i.handle));
});

sortEl?.addEventListener('change', draw);
stockBtn?.addEventListener('click', () => {
  inStockOnly = !inStockOnly;
  stockBtn.setAttribute('aria-pressed', inStockOnly);
  draw();
});

document.querySelector('[data-wishlist-clear]')?.addEventListener('click', () => {
  items.map((i) => i.handle).forEach((h) => toggleWishlist(h));
});

document.querySelector('[data-wishlist-add-all]')?.addEventListener('click', async (event) => {
  const button = event.currentTarget;
  const buyable = items.filter((i) => i.product.available && i.product.variants.length === 1);
  if (!buyable.length) {
    showToast(theme.strings.selectOptions || 'Choose options on each product first');
    return;
  }
  button.setAttribute('aria-busy', 'true');
  const body = new FormData();
  buyable.forEach((item, index) => {
    body.append(`items[${index}][id]`, item.product.variants[0].id);
    body.append(`items[${index}][quantity]`, 1);
  });
  await addToCart(body);
  button.removeAttribute('aria-busy');
});

document.querySelector('[data-wishlist-share]')?.addEventListener('click', async () => {
  const url = `${window.location.origin}${window.location.pathname}?items=${items.map((i) => i.handle).join(',')}`;
  try {
    if (navigator.share) await navigator.share({ title: document.title, url });
    else await navigator.clipboard.writeText(url);
    if (!navigator.share) showToast(theme.strings.linkCopied || 'Link copied');
  } catch (error) {
    /* share dismissed */
  }
});

init();
